import React, { FC, ReactElement, useState } from 'react';
import { Theme } from '@mui/material/styles';
import makeStyles from '@mui/styles/makeStyles';
import createStyles from '@mui/styles/createStyles';
import Button, { ButtonProps } from '@mui/material/Button';
import { clsx } from 'clsx';
import debounce from 'lodash/debounce';
// Components
import TooltipIf from '../tooltipIf';
import Loader from '../loader';
import Space, { Type } from '../layout/space';
// Resources
import { iconToCdn } from '../../utils/images';
import { IconAxel } from '../../resources/images';

export interface TextCenterButtonProps extends ButtonProps {
    title?: string;
    subtitle?: string;
    onClick: () => void;
    iconElement?: JSX.Element;
    className?: string;
    color?: any;
    customColor?: 'grey' | 'lightBlue' | 'blue' | 'green';
    variant?: 'text' | 'outlined' | 'contained';
    buttonSize?: 'xsmall' | 'small' | 'normal' | 'large';
    noFlex?: boolean;
    hover?: boolean;
    tooltip?: string;
    isLoading?: boolean;
    debounceTime?: number;
}

const TextCenterButton: FC<TextCenterButtonProps> = (props): ReactElement => {
    const {
        title,
        subtitle,
        onClick,
        iconElement,
        className,
        color = 'secondary',
        customColor,
        variant = 'contained',
        buttonSize = 'large',
        noFlex = false,
        hover = true,
        tooltip,
        isLoading = false,
        debounceTime = 500,
        ...rest
    } = props;

    const classes = useStyles();
    const [isActionInProgress, setIsActionInProgress] = useState(false);

    const action = debounce(
        () => {
            if (!isActionInProgress) {
                setIsActionInProgress(true);
                onClick();
                setTimeout(() => setIsActionInProgress(false), debounceTime); // Adjust the timeout to match debounce delay
            }
        },
        debounceTime,
        { leading: true, trailing: false },
    );

    let classCustomColor = null;
    if (color === 'inherit' || customColor === 'grey') {
        classCustomColor = classes.grey;
    }
    if (customColor === 'lightBlue') {
        classCustomColor = classes.lightBlue;
    }
    if (customColor === 'green') {
        classCustomColor = classes.green;
    }

    let classCustom = null;
    if (noFlex) {
        classCustom = classes.noFlex;
    }
    if (!hover) {
        classCustom = clsx(classCustom, classes.noHover);
    }

    return (
        <TooltipIf title={tooltip}>
            <Button
                onClick={isLoading ? null : action}
                className={clsx(
                    classes.textCenterButtonButton,
                    classCustom,
                    classes[buttonSize],
                    classCustomColor,
                    className,
                )}
                color={color}
                variant={variant}
                {...rest}
            >
                {isLoading ? (
                    <Loader size={20} />
                ) : (
                    <>
                        {subtitle ? (
                            <div className={classes.textContainer}>
                                {title}
                                <div className={classes.subtitle}>{subtitle}</div>
                            </div>
                        ) : (
                            title
                        )}
                        {(!!title || !!subtitle) && iconElement && <Space type={Type.horizontal} value={1} />}
                        {iconElement}
                    </>
                )}
            </Button>
        </TooltipIf>
    );
};

const useStyles = makeStyles((theme: Theme) =>
    createStyles({
        noFlex: {
            flex: 'unset!important',
        },
        noHover: {
            '&:hover': {
                boxShadow: 'unset',
                background: `unset!important`,
            },
        },
        textCenterButtonButton: {
            flex: 1,
            fontSize: 16,
            fontWeight: 900,
            borderRadius: 8,
            padding: '16px 24px',
            minWidth: 'fit-content',
            color: theme.palette.colors.fixed.darkWhite,
            '&.MuiButton-root.Mui-disabled': {
                opacity: 0.3,
                color: theme.palette.primary.contrastText,
                backgroundColor: theme.palette.colors.background.primaryBtn,
            },
            // Follow the button label instead of a fixed white: the disabled background is blue in light mode but
            // near-white in dark mode, where a white spinner is invisible against it
            '& .MuiCircularProgress-root': {
                color: 'inherit',
            },
        },
        grey: {
            boxShadow: 'unset',
            color: theme.palette.primary.main,
            backgroundColor: theme.palette.colors.background.primaryElement,
            '&:hover': {
                boxShadow: 'unset',
                background: `linear-gradient(0deg, rgba(0, 0, 0, 0.04), rgba(0, 0, 0, 0.04)), ${theme.palette.colors.background.primaryElement}`,
            },
            '&.MuiButton-root.Mui-disabled': {
                color: `${theme.palette.primary.main}c4`,
                backgroundColor: theme.palette.colors.background.primaryElement,
            },
        },
        lightBlue: {
            boxShadow: 'unset',
            backgroundColor: theme.palette.colors.background.accentBtn,
            '&:hover': {
                boxShadow: 'unset',
                background: `linear-gradient(0deg, rgba(255, 255, 255, 0.1), rgba(255, 255, 255, 0.1)), ${theme.palette.colors.background.accentBtn}`,
            },
            '&.MuiButton-root.Mui-disabled': {
                backgroundColor: `${theme.palette.colors.background.accentBtn}c4`,
            },
        },
        blue: {
            boxShadow: 'unset',
            backgroundColor: theme.palette.secondary.main,
            '&:hover': {
                boxShadow: 'unset',
                background: `linear-gradient(0deg, rgba(255, 255, 255, 0.1), rgba(255, 255, 255, 0.1)), ${theme.palette.colors.background.primaryBtn}`,
            },
            '&.MuiButton-root.Mui-disabled': {
                backgroundColor: `${theme.palette.secondary.main}c4`,
            },
        },
        green: {
            boxShadow: 'unset',
            background: 'linear-gradient(86.74deg, #119957 -25.89%, #0B6339 96.53%)',
            '&:hover': {
                boxShadow: 'unset',
                background: `linear-gradient(86.74deg, rgba(17, 153, 87, 0.9) -25.89%, rgba(11, 99, 57, 0.9) 96.53%)`,
            },
            '&.MuiButton-root.Mui-disabled': {
                background: 'linear-gradient(86.74deg, rgba(17, 153, 87, 0.5) -25.89%, rgba(11, 99, 57, 0.5) 96.53%)',
                // The gradient stays dark green in both modes, so the label and spinner must stay light
                color: theme.palette.colors.fixed.darkWhite,
            },
            '&::after': {
                content: '""',
                position: 'absolute',
                bottom: '-4px', // Distance from the bottom edge
                right: '5px', // Distance from the right edge
                width: '40px', // Adjust based on the icon size
                height: '40px', // Adjust based on the icon size

                maskImage: `url('${iconToCdn(IconAxel.Leaf)}')`,
                maskRepeat: 'no-repeat',
                maskSize: 'contain',

                WebkitMaskImage: `url('${iconToCdn(IconAxel.Leaf)}')`,
                WebkitMaskRepeat: 'no-repeat',
                WebkitMaskSize: 'contain',

                backgroundColor: '#FFFFFF80',
            },
        },
        large: {
            height: 56,
            maxHeight: 56,
        },
        normal: {
            height: 49,
            maxHeight: 49,
        },
        small: {
            height: 40,
            maxHeight: 40,
        },
        xsmall: {
            height: 30,
            maxHeight: 30,
        },
        textContainer: {
            flexDirection: 'column',
        },
        subtitle: {
            fontSize: 12,
            fontWeight: 500,
            marginTop: -7,
        },
    }),
);

export default TextCenterButton;
